import { type PermanentRejectionReason, isPermanentRejection } from "../types"
import { attributeReferralCode } from "./referralAPI"

export type AttributeOutcome =
  | { type: "success"; attempts: number }
  | { type: "permanent"; reason: PermanentRejectionReason; attempts: number }
  | { type: "exhausted"; attempts: number }
  | { type: "aborted" }

// First entry is immediate; later entries are pre-attempt delays.
const ATTEMPT_DELAYS_MS = [0, 1_000, 2_500] as const

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const t = setTimeout(resolve, ms)
    signal.addEventListener(
      "abort",
      () => {
        clearTimeout(t)
        resolve()
      },
      { once: true }
    )
  })
}

export async function attributeWithRetries(
  code: string,
  signal: AbortSignal
): Promise<AttributeOutcome> {
  let attempts = 0
  for (const delay of ATTEMPT_DELAYS_MS) {
    if (delay > 0) await sleep(delay, signal)
    if (signal.aborted) return { type: "aborted" }

    try {
      attempts += 1
      const data = await attributeReferralCode(code)
      if (signal.aborted) return { type: "aborted" }
      if (data.attributed) return { type: "success", attempts }
      if (isPermanentRejection(data.reason)) {
        return { type: "permanent", reason: data.reason, attempts }
      }
    } catch {
      if (signal.aborted) return { type: "aborted" }
    }
  }
  return { type: "exhausted", attempts }
}
